import React, { useCallback, useEffect, useState } from "react";
import { Button, DataTable, DataTableEmptyState } from "@okta/odyssey-react-mui";
import { DeleteIcon } from "@okta/odyssey-react-mui/icons";
import { useAuth0 } from "@auth0/auth0-react";
import { removeGuest } from '../../services/DemoAPI';
import { TimeAgo } from "../../utils";
import { useOpportunityContext } from "../../context/OpportunityContext";
import { useTranslation } from "react-i18next";

const InvitedGuestDataTable = ({helpLink, guests, setGuests, getGuests, onError}) => {

  const {opportunities} = useOpportunityContext()
  const {t} = useTranslation();

  const { isAuthenticated, getAccessTokenSilently } = useAuth0();

  const [pendingDeleteEmails, setPendingDeleteEmails] = useState(new Set());

 const deleteGuests = useCallback(async (guestsToDeleteEmails) => {
   const currentCallEmails = [...guestsToDeleteEmails];

   setPendingDeleteEmails(prev => new Set([...prev, ...currentCallEmails]));

   try {
     const accessToken = await getAccessTokenSilently();
     await Promise.all(currentCallEmails.map((email) => removeGuest(accessToken, email)));

     setPendingDeleteEmails(prev => {
       const next = new Set(prev);
       currentCallEmails.forEach(email => next.delete(email));
       return next;
     });

     setGuests(currentGuests =>
       currentGuests.filter(guest => !currentCallEmails.includes(guest.email))
     );

   } catch (error) {
     console.error("Failed to delete guests:", error);
     setPendingDeleteEmails(prev => {
       const next = new Set(prev);
       currentCallEmails.forEach(email => next.delete(email));
       return next;
     });
     onError(error);
   }
 }, [getAccessTokenSilently, onError, setPendingDeleteEmails, setGuests]);

 function rowMenuItems(row) {
   const email = row.getValue('email');
   const isDeleting = pendingDeleteEmails.has(email);
   return <Button
            variant='floating'
            startIcon={<DeleteIcon sx={{color:'black'}} />}
            onClick={() => deleteGuests([email])}
            disabled={isDeleting}
            aria-label={isDeleting ? t('guests-table-deleting-label', 'Deleting...') : t('guests-table-delete-label', 'Delete')}
          />;
 }

 function bulkActionMenuItems(rows) {
   const guestsToDelete = Object.keys(rows);
   const isAnyDeleting = guestsToDelete.some(email => pendingDeleteEmails.has(email));
   const hasSelection = guestsToDelete.length > 0;
   return [<Button
            variant="danger"
            label={t('guests-table-delete-bulk-label')}
            startIcon={<DeleteIcon/>}
            onClick={() => deleteGuests(guestsToDelete)}
            disabled={isAnyDeleting || !hasSelection}
          />]
 }

 const filterData = useCallback(({data, ...args}) => {
   let filteredData = data;
   const { search, sort } = args;

   if (pendingDeleteEmails.size > 0) {
        filteredData = filteredData.filter(guest => !pendingDeleteEmails.has(guest.email));
   }

   // Implement text-based query filtering
   if (search) {
     filteredData = filteredData.filter((row) =>
       Object.values(row).some((value) =>
         value && value.toString().toLowerCase().includes(search.toLowerCase()),
       ),
     );
   }

   // Implement sorting
   if (sort && sort.length > 0) {
     filteredData = filteredData.slice().sort((a, b) => {
       for (const { id, desc } of sort) {
         const aValue = a[id];
         const bValue = b[id];

         if (aValue < bValue) return desc ? 1 : -1;
         if (aValue > bValue) return desc ? -1 : 1;
       }

       return 0;
     });
   }

   return filteredData;
 },[pendingDeleteEmails]);

 const getData = useCallback(({search,sort}) => {
  return filterData({ data: guests, search, sort });
},
[guests, filterData],
);

 useEffect(() => {
   if (isAuthenticated) {
       getGuests()
   } else {
       setGuests([])
       setPendingDeleteEmails(new Set());
   }
 }, [getGuests, isAuthenticated, setGuests])

   const columns = [
     {
       accessorKey: "email",
       header: t('guests-table-header-email'),
     },
     {
       Cell: ({ cell }) => opportunities?.find((op) => op.opportunity_id === cell.getValue())?.name || t("guests-table-header-opportunity-none"),
       accessorKey: "opportunityId",
       header: t('guests-table-header-opportunity'),
     },
     {
       Cell: ({ cell }) => t('guests-table-cell-access-length', {count: cell.getValue()}),
       accessorKey: "expiresAfter",
       header: t('guests-table-header-access-length'),
     },
     {
       Cell: ({ cell }) => cell.getValue() ? TimeAgo.format(new Date(cell.getValue()), 'round-minute'): '',
       accessorKey: "mustRegisterBy",
       header: t('guests-table-header-invite-expiry'),
     },
   ]
   return (
           <DataTable
             columns={columns}
             getData={getData}
             hasFilters={false}
             hasPagination={false}
             hasRowSelection
             getRowId={(row) => row.email}
             hasSearch
             hasColumnVisibility
             hasSorting={false}
             emptyPlaceholder={
               <DataTableEmptyState
                 heading={t('guests-table-empty-state-heading')}
                 text={<>{t('guests-table-empty-state-text')} {helpLink}</>}
               />
             }
             noResultsPlaceholder={<DataTableEmptyState
               heading={t('guests-table-no-results-heading')}
               text={t('guests-table-no-results-text')}
             />}
             rowActionButtons={rowMenuItems}
             bulkActionMenuItems={bulkActionMenuItems}
           />
   );
 };
 export default InvitedGuestDataTable;